'use client';

import { Chip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { PENDING_CONVERSATION, reelsTurnStore, useVideoStudy } from '../reels-turn-store';

/** Landing-page params set by "Ask DANI about these videos" in the Multimedia tab. */
export const VIDEO_STUDY_PARAMS = {
    PROJECT: 'videos',
    SURVEYS: 'surveys',
    QUESTION: 'q',
    NAME: 'name',
} as const;

/**
 * "Videos: <study>": shows that this conversation's turns go to the video
 * agent, and removing it sends the next turn to DANI as usual.
 */
export function VideoStudyChip({ conversationId }: { conversationId: string | null }) {
    const study = useVideoStudy(conversationId);
    if (!study) return null;
    return (
        <div className="flex">
            <Chip
                size="sm"
                variant="flat"
                color="primary"
                startContent={<Icon icon="lucide:video" width={14} className="ml-1" />}
                onClose={() =>
                    reelsTurnStore.setStudy(conversationId ?? PENDING_CONVERSATION, null)
                }
                classNames={{ content: 'max-w-[420px] truncate' }}
            >
                Videos: {study.projectName}
                {study.questionText ? ` · ${study.questionText}` : ''}
            </Chip>
        </div>
    );
}

/**
 * On the landing page: attaches the study named in the URL to the chat about
 * to start, or clears a stale one when the URL names none. The name is only a
 * label until the first answer replaces it with the server's own lookup.
 */
export function usePendingVideoStudyFromUrl(): boolean {
    const searchParams = useSearchParams();
    const projectId = searchParams.get(VIDEO_STUDY_PARAMS.PROJECT);
    const surveys = searchParams.get(VIDEO_STUDY_PARAMS.SURVEYS);
    const questionId = searchParams.get(VIDEO_STUDY_PARAMS.QUESTION);
    const name = searchParams.get(VIDEO_STUDY_PARAMS.NAME);

    useEffect(() => {
        if (!projectId) {
            reelsTurnStore.setStudy(PENDING_CONVERSATION, null);
            return;
        }
        reelsTurnStore.setStudy(PENDING_CONVERSATION, {
            projectId,
            projectName: name || 'this study',
            surveyIds: (surveys ?? '')
                .split(',')
                .map((s) => s.trim())
                .filter((s) => /^\d+$/.test(s)),
            ...(questionId ? { questionId } : {}),
        });
    }, [projectId, surveys, questionId, name]);

    return !!projectId;
}
